import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { OptionList } from '../wizard/OptionList'
import { SearchableOptions } from '../wizard/SearchableOptions'
import { SkeletonCards } from '../ui/Skeleton'
import { PlateField } from '../ui/PlateField'
import { toPersianDigits } from '../../lib/fa'
import { currentJalaliYear } from '../../lib/jalali'
import { EMPTY_PLATE, isValidPlate, type Plate } from '../../lib/plate'
import { getReference } from '../../lib/quotes-api'
import type { SaveVehicleBody, SavedVehicle } from '../../lib/vehicles-api'

/** Nothing older is insurable in practice, and the input schema stops at 1350 regardless. */
const OLDEST_PRODUCTION_YEAR = 1350

export type VehicleStepId = 'model' | 'usage' | 'identity'

/**
 * Registering a vehicle, apart from insuring it.
 *
 * What a car *is* — model, usage, production year, plate — does not change from one policy to
 * the next, so it is asked once and saved, and the quote flow only asks what is per-policy. The
 * same steps run inside the motor wizard (for a car not yet saved) and on their own from «خودروهای
 * من», which is why they live here as a hook rather than inside either page.
 *
 * A motorcycle can only be insured for personal use, so asking is a question with one answer —
 * the screen is dropped rather than shown disabled, which keeps the progress bar honest.
 */
export function useVehicleDraft() {
  const [modelId, setModelId] = useState<string | null>(null)
  const [usage, setUsage] = useState<string | null>(null)
  const [productionYear, setProductionYear] = useState('')
  const [plate, setPlate] = useState<Plate>(EMPTY_PLATE)

  const models = useQuery({
    queryKey: ['ref', 'vehicle-models'],
    queryFn: () => getReference('vehicle-models'),
    // The catalog does not change mid-session; re-fetching it between steps is pure latency.
    staleTime: Infinity,
  })
  const usages = useQuery({
    queryKey: ['ref', 'vehicle-usages'],
    queryFn: () => getReference('vehicle-usages'),
  })

  const model = models.data?.find((m) => m.value === modelId)
  const group = model?.meta?.group as string | undefined
  const isMotorcycle = group === 'MOTORCYCLE'
  const effectiveUsage = isMotorcycle ? 'PERSONAL' : usage

  const steps: VehicleStepId[] = isMotorcycle ? ['model', 'identity'] : ['model', 'usage', 'identity']

  const complete: Record<VehicleStepId, boolean> = {
    model: modelId !== null,
    usage: usage !== null,
    identity: productionYear !== '' && isValidPlate(plate),
  }

  const load = (vehicle: SavedVehicle) => {
    setModelId(vehicle.vehicleModelId)
    setUsage(vehicle.usage)
    setProductionYear(String(vehicle.productionYear))
    setPlate(vehicle.plate)
  }

  const body = (): SaveVehicleBody => ({
    vehicleModelId: modelId as string,
    plate,
    productionYear: Number(productionYear),
    usage: effectiveUsage as string,
  })

  const render = (step: VehicleStepId): ReactNode => {
    switch (step) {
      case 'model':
        return models.data ? (
          <SearchableOptions
            options={models.data}
            value={modelId}
            onChange={setModelId}
            placeholderFa="جست‌وجوی خودرو یا برند…"
          />
        ) : (
          <SkeletonCards count={4} height="h-14" className="space-y-2.5" />
        )
      case 'usage':
        return usages.data ? (
          <OptionList options={usages.data} value={usage} onChange={setUsage} />
        ) : (
          <SkeletonCards count={4} height="h-14" className="space-y-2.5" />
        )
      case 'identity':
        return (
          <div className="space-y-6">
            <YearSelect value={productionYear} onChange={setProductionYear} />
            <PlateField value={plate} onChange={setPlate} />
          </div>
        )
    }
  }

  return {
    modelId,
    group,
    isMotorcycle,
    effectiveUsage,
    productionYear,
    plate,
    steps,
    complete,
    load,
    body,
    render,
  }
}

export const VEHICLE_STEP_TITLES: Record<VehicleStepId, string> = {
  model: 'چه خودرویی دارید؟',
  usage: 'کاربری خودرو چیست؟',
  identity: 'مشخصات خودرو',
}

export const VEHICLE_STEP_HINTS: Record<VehicleStepId, string> = {
  model: 'برند یا نام خودرو را جست‌وجو کنید.',
  usage: 'کاربری روی نرخ بیمه اثر مستقیم دارد.',
  identity: 'سال ساخت و شماره پلاک را همان‌طور که روی کارت خودرو نوشته شده وارد کنید.',
}

export const selectClass =
  'w-full rounded-2xl border border-line bg-card px-4 py-3.5 text-base text-strong outline-none transition-colors focus:border-brand-500'

/** A list, not a number field: the year is on the card in front of them, ready to pick. */
function YearSelect({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  // Next year is a real answer — cars are sold ahead of their model year.
  const newest = currentJalaliYear() + 1
  const years = Array.from({ length: newest - OLDEST_PRODUCTION_YEAR + 1 }, (_, i) => newest - i)

  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-strong">سال ساخت</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={selectClass}
      >
        <option value="">انتخاب کنید</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {toPersianDigits(year)}
          </option>
        ))}
      </select>
    </label>
  )
}
